import { daysFromToday, formatBytes, formatDayTime, formatTime, toDate } from '@/lib/format';

// Formats the backups screens need beyond the shared helpers, in the same English (UK) style.

type DateInput = Date | string | number;

const monthYearFmt = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });

/** "Today 09:00", "Yesterday 17:46", "Sun 11 Oct 03:00", as the backup history lists them. */
export function backupWhen(d: DateInput): string {
  const date = toDate(d);
  const days = daysFromToday(date);
  if (days === 0) return `Today ${formatTime(date)}`;
  if (days === -1) return `Yesterday ${formatTime(date)}`;
  return formatDayTime(date);
}

/** backupWhen inside a sentence: "the backup from today 09:00". */
export function backupWhenInline(d: DateInput): string {
  const s = backupWhen(d);
  return s.startsWith('Today') || s.startsWith('Yesterday') ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

/** "October 2026", the default name for a new key pair. */
export function monthYear(d: DateInput = new Date()): string {
  return monthYearFmt.format(toDate(d));
}

/** "412 MB" as ["412", "MB"], for a big number with a small unit. */
export function sizeParts(bytes: number): [string, string] {
  const s = formatBytes(bytes);
  const i = s.lastIndexOf(' ');
  return [s.slice(0, i), s.slice(i + 1)];
}

/** "0.4 s", "38 s", "2 min 5 s", "1 h 4 min". */
export function formatDuration(ms: number): string {
  if (ms < 10_000) return `${(Math.max(ms, 100) / 1000).toFixed(1).replace(/\.0$/, '')} s`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs} s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return secs % 60 ? `${mins} min ${secs % 60} s` : `${mins} min`;
  const hours = Math.floor(mins / 60);
  return mins % 60 ? `${hours} h ${mins % 60} min` : `${hours} h`;
}

/** formatDuration split for the stat line: ["38", "s"], or the whole text when it has several parts. */
export function durationParts(ms: number): [string, string] {
  const s = formatDuration(ms);
  const parts = s.split(' ');
  return parts.length === 2 ? [parts[0]!, parts[1]!] : [s, ''];
}

/** "1st", "2nd", "23rd" for days of the month. */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const last = n % 10;
  return `${n}${last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th'}`;
}

/** Percentage for progress: "45%". */
export function percent(fraction: number | null | undefined): string {
  return `${Math.round(Math.max(0, Math.min(1, fraction ?? 0)) * 100)}%`;
}

/** "$0.0245/GB", the S3 price per GB-month with as many decimals as AWS lists. */
export function formatPricePerGb(usd: number): string {
  return `$${String(Number(usd.toFixed(5)))}/GB`;
}

/** "about 2 minutes" for how long a restore may take, from the latest backup's duration. */
export function restoreTime(latestBackupMs: number | null | undefined): string {
  if (!latestBackupMs) return 'a few minutes';
  // Checking the file, the fresh backup first and writing the rows each take about as long as one backup.
  const mins = Math.round((latestBackupMs * 3) / 60_000);
  if (mins <= 1) return 'about a minute';
  if (mins < 60) return `about ${mins} minutes`;
  const hours = Math.round(mins / 60);
  return hours === 1 ? 'about an hour' : `about ${hours} hours`;
}
